// ─────────────────────────────────────────────────────────────────
// Avstånd och riktning för Q i jakten (QA1):
//  - "Hur långt bort står Ola och åt vilket håll?" → haversine + åtta väderstreck,
//    och "till höger" o.s.v. när personens färdriktning (heading) är känd.
//  - "100 m norr om mig ser jag två rådjur" → observationen läggs 100 m norr om den
//    som pratar. Avstånd i meter, kilometer och steg; riktning i väderstreck eller
//    framåt/höger/bakåt/vänster (kräver färdriktning).
// Kopia av API:ts apps/api/src/lib/geo.ts (ändra båda): det Q hör utan "Q, …" placeras här.
// ─────────────────────────────────────────────────────────────────

export type LatLng = { lat: number; lng: number };

const R = 6_371_000;
const rad = (d: number) => (d * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;
const norm360 = (d: number) => ((d % 360) + 360) % 360;

/** Fågelvägen i meter (haversine). */
export function haversineM(a: LatLng, b: LatLng): number {
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Kompassriktning från a till b i grader (0 = norr, 90 = öster). */
export function bearingDeg(a: LatLng, b: LatLng): number {
  const y = Math.sin(rad(b.lng - a.lng)) * Math.cos(rad(b.lat));
  const x = Math.cos(rad(a.lat)) * Math.sin(rad(b.lat)) - Math.sin(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.cos(rad(b.lng - a.lng));
  return norm360(deg(Math.atan2(y, x)));
}

/** Punkten `meters` bort från `from` i riktningen `bearing` (grader från norr). */
export function destination(from: LatLng, bearing: number, meters: number): LatLng {
  const d = meters / R;
  const b = rad(bearing);
  const lat1 = rad(from.lat);
  const lng1 = rad(from.lng);
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(b));
  const lng2 = lng1 + Math.atan2(Math.sin(b) * Math.sin(d) * Math.cos(lat1), Math.cos(d) - Math.sin(lat1) * Math.sin(lat2));
  return { lat: deg(lat2), lng: ((deg(lng2) + 540) % 360) - 180 };
}

export const COMPASS8 = ['norr', 'nordost', 'öster', 'sydost', 'söder', 'sydväst', 'väster', 'nordväst'] as const;

/** Grader → ett av åtta väderstreck. */
export function compass8(bearing: number): (typeof COMPASS8)[number] {
  return COMPASS8[Math.round(norm360(bearing) / 45) % 8];
}

const SIDES = ['rakt fram', 'snett fram till höger', 'till höger', 'snett bakom till höger', 'rakt bakom', 'snett bakom till vänster', 'till vänster', 'snett fram till vänster'] as const;

/** Riktningen sett från personen som går/tittar åt `heading`: "till höger". */
export function relativeSide(bearing: number, heading: number): (typeof SIDES)[number] {
  return SIDES[Math.round(norm360(bearing - heading) / 45) % 8];
}

/** "80 m", "1,2 km" (avrundat så att det låter rimligt i örat). */
export function formatDistance(m: number): string {
  if (m < 1000) return `${Math.max(5, Math.round(m / (m < 100 ? 5 : 10)) * (m < 100 ? 5 : 10))} m`;
  return `${(Math.round(m / 100) / 10).toString().replace('.', ',')} km`;
}

// ── Tolkning av talat avstånd och riktning ───────────────────────

/** Ett steg ≈ 0,75 m. */
export const STEP_M = 0.75;
/** Längre bort än så här placeras inte en observation (felhört eller orimligt). */
export const MAX_OFFSET_M = 5000;

const ONES: Record<string, number> = {
  en: 1, ett: 1, två: 2, tre: 3, fyra: 4, fem: 5, sex: 6, sju: 7, åtta: 8, nio: 9, tio: 10, elva: 11, tolv: 12,
  tretton: 13, fjorton: 14, femton: 15, sexton: 16, sjutton: 17, arton: 18, nitton: 19,
};
const TENS: Record<string, number> = { tjugo: 20, trettio: 30, fyrtio: 40, femtio: 50, sextio: 60, sjuttio: 70, åttio: 80, nittio: 90 };

/** "tjugofem" → 25. */
function below100(w: string): number | null {
  if (!w) return 0;
  if (w in ONES) return ONES[w];
  for (const [t, v] of Object.entries(TENS)) {
    if (w === t) return v;
    if (w.startsWith(t) && w.slice(t.length) in ONES && ONES[w.slice(t.length)] < 10) return v + ONES[w.slice(t.length)];
  }
  return null;
}

/** Svenska räkneord upp till tusentals: "tvåhundrafemtio" → 250, "hundra" → 100, "ett par hundra" ej här. */
export function swedishNumber(word: string): number | null {
  const w = word.toLowerCase().replace(/[\s-]/g, '');
  if (!w) return null;
  const tusen = w.indexOf('tusen');
  if (tusen >= 0) {
    const head = w.slice(0, tusen);
    const k = head ? swedishNumber(head) : 1;
    const rest = w.slice(tusen + 5);
    const r = rest ? swedishNumber(rest) : 0;
    return k == null || r == null ? null : k * 1000 + r;
  }
  const hundra = w.indexOf('hundra');
  if (hundra >= 0) {
    const head = w.slice(0, hundra);
    const h = head ? below100(head) : 1;
    const r = below100(w.slice(hundra + 6));
    return h == null || r == null ? null : h * 100 + r;
  }
  return below100(w) || null;
}

const L = '\\p{L}';
const NUM_WORD = `(?:${[...Object.keys(ONES), ...Object.keys(TENS), 'hundra', 'tusen'].join('|')})[${L}]*`;
const DIST_RE = new RegExp(
  `(?<![${L}\\d])(?:(ett\\s+par|ett\\s+fåtal|några)\\s+)?(\\d+(?:[.,]\\d+)?|halv|${NUM_WORD}(?:\\s+${NUM_WORD})?)\\s*(kilometer|km|meter|metrar|m|steg)(?![${L}])`,
  'giu',
);

/** "100 m" / "hundra meter" / "1,5 km" / "en halv kilometer" / "50 steg" / "ett par hundra meter" → meter. */
export function parseDistance(text: string): number | null {
  const t = text.toLowerCase().replace(/(?<![\p{L}])en\s+halv(?![\p{L}])/gu, 'halv');
  // Första träffen där talet går att tolka ("enbuske 50 m" → 50).
  for (const m of t.matchAll(DIST_RE)) {
    const [, some, rawNum, unit] = m;
    let n: number | null;
    if (rawNum === 'halv') n = 0.5;
    else if (/^\d/.test(rawNum)) n = Number(rawNum.replace(',', '.'));
    else n = swedishNumber(rawNum);
    if (n == null || !Number.isFinite(n) || n <= 0) continue;
    if (some && n === 100) n = 200; // "ett par hundra meter"
    const factor = unit === 'km' || unit === 'kilometer' ? 1000 : unit === 'steg' ? STEP_M : 1;
    return n * factor;
  }
  return null;
}

// Längsta först: "nordost" före "norr", "sydväst" före "syd".
const COMPASS_WORDS: [RegExp, number][] = [
  [/nord\s*-?\s*(?:ost|öst|öster)/, 45],
  [/syd\s*-?\s*(?:ost|öst|öster)|söder\s*-?\s*öst/, 135],
  [/syd\s*-?\s*väst|söder\s*-?\s*väst/, 225],
  [/nord\s*-?\s*väst|norr\s*-?\s*väst/, 315],
  [/norr|nord/, 0],
  [/öster|öst|östra|österut/, 90],
  [/söder|syd|södra|söderut/, 180],
  [/väster|väst|västra|västerut/, 270],
];
const RELATIVE_WORDS: [RegExp, number][] = [
  [/snett\s+(?:fram(?:åt|för)?\s+)?(?:till|åt)\s+höger|snett\s+höger\s+fram/, 45],
  [/snett\s+(?:fram(?:åt|för)?\s+)?(?:till|åt)\s+vänster|snett\s+vänster\s+fram/, 315],
  [/snett\s+bak(?:åt|om(?:\s+mig)?)?\s+(?:till|åt)\s+höger/, 135],
  [/snett\s+bak(?:åt|om(?:\s+mig)?)?\s+(?:till|åt)\s+vänster/, 225],
  [/(?:till|åt|på)\s+(?:min\s+)?högers?(?:\s+sida)?|höger\s+om\s+mig/, 90],
  [/(?:till|åt|på)\s+(?:min\s+)?vänsters?(?:\s+sida)?|vänster\s+om\s+mig/, 270],
  [/bakom\s+mig|bakåt|rakt\s+bak/, 180],
  [/framför\s+mig|framåt|rakt\s+fram|fram\s+från\s+mig/, 0],
  // Ensamma ord sist ("100 m höger").
  [/höger(?![\p{L}])/u, 90],
  [/vänster(?![\p{L}])/u, 270],
  [/fram(?![\p{L}])/u, 0],
];

export type ParsedDirection =
  | { kind: 'compass'; bearing: number; label: string }
  | { kind: 'relative'; offset: number; label: string };

/** Riktningen i en mening: väderstreck ("nordost om mig") eller relativt ("till vänster"). */
export function parseDirection(text: string): ParsedDirection | null {
  const t = ` ${text.toLowerCase()} `;
  const wordAt = (re: RegExp) => new RegExp(`(?<![${L}])(?:${re.source})`, 'u');
  for (const [re, bearing] of COMPASS_WORDS) {
    if (wordAt(re).test(t)) return { kind: 'compass', bearing, label: compass8(bearing) };
  }
  for (const [re, offset] of RELATIVE_WORDS) {
    if (wordAt(re).test(t)) return { kind: 'relative', offset, label: SIDES[offset / 45] };
  }
  return null;
}

/** Kompassriktning för en tolkad riktning; relativ kräver färdriktning (annars null). */
export function directionBearing(d: ParsedDirection, heading?: number | null): number | null {
  if (d.kind === 'compass') return d.bearing;
  return heading == null || !Number.isFinite(heading) ? null : norm360(heading + d.offset);
}

/** label: "100 m norr om", side: "till vänster" när riktningen sades relativt. */
export interface Offset { meters: number; bearing: number; label: string; side?: string }
export type OffsetResult = { ok: true; offset: Offset } | { ok: false; reason: string } | null;

/**
 * "100 m norr om mig ser jag två rådjur" → 100 m åt 0°. null när meningen inte anger både avstånd och riktning.
 * Relativ riktning utan färdriktning, eller orimligt avstånd, ger ok: false med skälet.
 */
export function parseOffset(text: string, heading?: number | null): OffsetResult {
  const meters = parseDistance(text);
  const dir = parseDirection(text);
  if (meters == null || !dir) return null;
  return offsetFrom(meters, dir, heading);
}

export function offsetFrom(meters: number, dir: ParsedDirection, heading?: number | null): Exclude<OffsetResult, null> {
  if (meters > MAX_OFFSET_M) return { ok: false, reason: `${formatDistance(meters)} är för långt bort för att placera observationen (högst ${formatDistance(MAX_OFFSET_M)}).` };
  const bearing = directionBearing(dir, heading);
  if (bearing == null) return { ok: false, reason: `Riktningen "${dir.label}" kräver att din färdriktning är känd. Säg ett väderstreck i stället, t.ex. norr eller nordost.` };
  return { ok: true, offset: { meters, bearing, label: `${formatDistance(meters)} ${compass8(bearing)} om`, ...(dir.kind === 'relative' ? { side: dir.label } : {}) } };
}
